import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { leadsAPI, supabase } from '../../lib/supabase.js';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiUser, FiMail, FiPhone, FiCar, FiDollarSign, FiCalendar, FiEdit, FiTrash2, FiPlus, FiFilter, FiSearch, FiMessageSquare, FiUserPlus, FiCheck } = FiIcons;

const LeadManagement = () => {
  const [leads, setLeads] = useState([]);
  const [contactInquiries, setContactInquiries] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('leads');
  const [editMode, setEditMode] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    lead_type: '',
    priority: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [newNote, setNewNote] = useState('');
  const [newLead, setNewLead] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    lead_type: 'information',
    status: 'new',
    priority: 'medium',
    vehicle_interest: '',
    budget_range: '',
    notes: ''
  });

  useEffect(() => {
    loadLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, filters, searchTerm]);

  const loadLeads = async () => {
    try {
      const data = await leadsAPI.getLeads();
      setLeads(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading leads:', error);
      setLoading(false);
    }
  };

  // Map contact inquiry types to valid lead types
  const mapInquiryTypeToLeadType = (type) => {
    const t = (type || '').toLowerCase();
    switch (t) {
      case 'service':
        return 'service';
      case 'parts':
        return 'parts';
      case 'test_drive':
        return 'test_drive';
      case 'financing':
        return 'financing';
      case 'trade_in':
        return 'trade_in';
      // Map everything else (e.g., 'general', 'sales', 'complaint', 'compliment') to 'information'
      default:
        return 'information';
    }
  };



  const filterLeads = () => {
    let filtered = leads;

    // Filter leads
    if (filters.status) {
      filtered = filtered.filter(lead => lead.status === filters.status);
    }
    if (filters.lead_type) {
      filtered = filtered.filter(lead => lead.lead_type === filters.lead_type);
    }
    if (filters.priority) {
      filtered = filtered.filter(lead => lead.priority === filters.priority);
    }
    if (searchTerm) {
      filtered = filtered.filter(lead => 
        (lead.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.message || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (lead.vehicle_make && lead.vehicle_make.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.vehicle_model && lead.vehicle_model.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredLeads(filtered);
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      await leadsAPI.updateLead(leadId, { status: newStatus });
      loadLeads();
    } catch (error) {
      console.error('Error updating lead status:', error);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (window.confirm('Are you sure you want to delete this lead?')) {
      try {
        await leadsAPI.deleteLead(leadId);
        loadLeads();
      } catch (error) {
        console.error('Error deleting lead:', error);
      }
    }
  };



  const handleAddLead = async () => {
    try {
      const leadData = {
        firstName: newLead.first_name,
        lastName: newLead.last_name,
        email: newLead.email,
        phone: newLead.phone,
        leadType: newLead.lead_type,
        status: newLead.status,
        priority: newLead.priority,
        vehicleInterest: newLead.vehicle_interest,
        budgetRange: newLead.budget_range,
        message: newLead.notes
      };
      await leadsAPI.addLead(leadData);
      loadLeads();
      setShowAddModal(false);
      // Reset form
      setNewLead({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        lead_type: 'information',
        status: 'new',
        priority: 'medium',
        vehicle_interest: '',
        budget_range: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding lead:', error);
      alert('Failed to add lead. Please try again.');
    }
  };

  const handleAddNote = async (leadId) => {
    if (!newNote.trim()) return;
    
    try {
      await leadsAPI.addNote(leadId, newNote);
      setNewNote('');
      loadLeads();
      // Refresh selected lead if it's the one we added note to
      if (selectedLead && selectedLead.id === leadId) {
        const updatedLead = leads.find(l => l.id === leadId);
        setSelectedLead(updatedLead);
      }
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'converted': return 'bg-purple-100 text-purple-800';
      case 'lost': return 'bg-red-100 text-red-800';
      case 'follow_up': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getLeadTypeIcon = (type) => {
    switch (type) {
      case 'test_drive': return FiCar;
      case 'financing': return FiDollarSign;
      case 'trade_in': return FiCar;
      default: return FiUser;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi">Lead Management</h1>
        <p className="text-[13px] text-adm-text-mid">All customer inquiries and leads in one place</p>
      </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-adm-ctl flex items-center justify-center space-x-2 transition-colors w-full sm:w-auto">
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Lead</span>
        </button>
      </div>



      {/* Filters and Search */}
      <div className="bg-white rounded-lg p-4 sm:p-6 shadow-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
           
          >
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="qualified">Qualified</option>
            <option value="converted">Converted</option>
            <option value="lost">Lost</option>
            <option value="follow_up">Follow Up</option>
          </select>

          <select
            value={filters.lead_type}
            onChange={(e) => setFilters({...filters, lead_type: e.target.value})}
           
          >
            <option value="">All Types</option>
            <option value="test_drive">Test Drive</option>
            <option value="financing">Financing</option>
            <option value="trade_in">Trade-In</option>
            <option value="information">Information</option>
            <option value="service">Service</option>
            <option value="parts">Parts</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({...filters, priority: e.target.value})}
           
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Lead</th>
                <th>Type</th>
                <th>Vehicle Interest</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((item) => (
                <motion.tr
                  key={item.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedLead(item);
                    setShowModal(true);
                  }}
                >
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <SafeIcon icon={FiUser} className="w-5 h-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {`${item.first_name || ''} ${item.last_name || ''}`.trim() || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center space-x-2">
                          <SafeIcon icon={FiMail} className="w-3 h-3" />
                          <span>{item.email}</span>
                        </div>
                        {item.phone && (
                          <div className="text-sm text-gray-500 flex items-center space-x-2">
                            <SafeIcon icon={FiPhone} className="w-3 h-3" />
                            <span>{item.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <SafeIcon icon={getLeadTypeIcon(item.lead_type || 'information')} className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 capitalize">
                        {(item.lead_type || 'information').replace('_', ' ')}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.vehicle_make && item.vehicle_model ? (
                      <div>
                        <div>{item.vehicle_year} {item.vehicle_make} {item.vehicle_model}</div>
                        {item.budget_min && item.budget_max && (
                          <div className="text-xs text-gray-500">
                            Budget: ${item.budget_min?.toLocaleString()} - ${item.budget_max?.toLocaleString()}
                          </div>
                        )}
                      </div>
                    ) : item.message ? (
                      <div className="text-xs text-gray-500 truncate max-w-xs">{item.message}</div>
                    ) : (
                      <span className="text-gray-400">Not specified</span>
                    )}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status || 'new')}`}>
                      {(item.status || 'new').replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${getPriorityColor(item.priority || 'medium')}`}></div>
                      <span className="text-sm text-gray-900 capitalize">{item.priority || 'medium'}</span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLead(item);
                          setShowModal(true);
                        }}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLead(item.id);
                        }}
                        className="text-red-600 hover:text-red-900">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {showModal && selectedLead && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex items-start justify-center min-h-screen pt-8 px-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

              <motion.div
                className="relative bg-adm-bg-1 rounded-adm-modal shadow-adm-pop w-full max-w-4xl max-h-[85vh] overflow-y-auto"
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
              >
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-[15px] font-semibold text-adm-text-hi">
                      Lead Details: {`${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim()}
                    </h3>
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Lead Details */}
                    <div className="space-y-6">
                      {/* Edit/Save buttons */}
                      <div className="flex justify-end space-x-2">
                        {!editMode ? (
                          <button
                            onClick={() => {
                              setEditMode(true);
                              setEditingLead({...selectedLead});
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                          >
                            <SafeIcon icon={FiEdit} className="w-4 h-4" />
                            <span>Edit Lead</span>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditMode(false);
                                setEditingLead(null);
                              }}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const updateData = {
                                    first_name: editingLead.first_name,
                                    last_name: editingLead.last_name,
                                    email: editingLead.email,
                                    phone: editingLead.phone,
                                    lead_type: editingLead.lead_type,
                                    status: editingLead.status,
                                    priority: editingLead.priority,
                                    vehicle_make: editingLead.vehicle_make,
                                    vehicle_model: editingLead.vehicle_model,
                                    vehicle_year: editingLead.vehicle_year,
                                    budget_min: editingLead.budget_min,
                                    budget_max: editingLead.budget_max,
                                    preferred_contact_method: editingLead.preferred_contact_method,
                                    message: editingLead.message
                                  };
                                  
                                  await leadsAPI.updateLead(selectedLead.id, updateData);
                                  setSelectedLead(editingLead);
                                  setEditMode(false);
                                  loadLeads();
                                  alert('Lead updated successfully!');
                                } catch (error) {
                                  console.error('Error updating lead:', error);
                                  alert('Failed to update lead');
                                }
                              }}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                            >
                              <SafeIcon icon={FiCheck} className="w-4 h-4" />
                              <span>Save Changes</span>
                            </button>
                          </>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Contact Information */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">Contact Information</h4>
                          <div className="space-y-2">
                            {editMode ? (
                              <>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">First Name</label>
                                  <input
                                    type="text"
                                    value={editingLead.first_name || ''}
                                    onChange={(e) => setEditingLead({...editingLead, first_name: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Last Name</label>
                                  <input
                                    type="text"
                                    value={editingLead.last_name || ''}
                                    onChange={(e) => setEditingLead({...editingLead, last_name: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Email</label>
                                  <input
                                    type="email"
                                    value={editingLead.email || ''}
                                    onChange={(e) => setEditingLead({...editingLead, email: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Phone</label>
                                  <input
                                    type="text"
                                    value={editingLead.phone || ''}
                                    onChange={(e) => setEditingLead({...editingLead, phone: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <p><strong>Name:</strong> {selectedLead.first_name} {selectedLead.last_name}</p>
                                <p><strong>Email:</strong> {selectedLead.email}</p>
                                {selectedLead.phone && <p><strong>Phone:</strong> {selectedLead.phone}</p>}
                                <p><strong>Preferred Contact:</strong> {selectedLead.preferred_contact_method}</p>
                                {selectedLead.preferred_contact_time && (
                                  <p><strong>Best Time:</strong> {selectedLead.preferred_contact_time}</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Lead Information */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-gray-900">Lead Information</h4>
                          <div className="space-y-2">
                            {editMode ? (
                              <>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Type</label>
                                  <select
                                    value={editingLead.lead_type || ''}
                                    onChange={(e) => setEditingLead({...editingLead, lead_type: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  >
                                    <option value="test_drive">Test Drive</option>
                                    <option value="financing">Financing</option>
                                    <option value="trade_in">Trade-In</option>
                                    <option value="information">Information</option>
                                    <option value="service">Service</option>
                                    <option value="parts">Parts</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Status</label>
                                  <select
                                    value={editingLead.status || ''}
                                    onChange={(e) => setEditingLead({...editingLead, status: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  >
                                    <option value="new">New</option>
                                    <option value="contacted">Contacted</option>
                                    <option value="qualified">Qualified</option>
                                    <option value="converted">Converted</option>
                                    <option value="lost">Lost</option>
                                    <option value="follow_up">Follow Up</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="text-sm font-medium text-gray-700">Priority</label>
                                  <select
                                    value={editingLead.priority || ''}
                                    onChange={(e) => setEditingLead({...editingLead, priority: e.target.value})}
                                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  >
                                    <option value="urgent">Urgent</option>
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                  </select>
                                </div>
                              </>
                            ) : (
                              <>
                                <p><strong>Type:</strong> {selectedLead.lead_type?.replace('_', ' ')}</p>
                                <p><strong>Source:</strong> {selectedLead.lead_source}</p>
                                <p><strong>Status:</strong> {selectedLead.status}</p>
                                <p><strong>Priority:</strong> {selectedLead.priority}</p>
                                <p><strong>Created:</strong> {new Date(selectedLead.created_at).toLocaleString()}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Vehicle Interest */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Vehicle Interest</h4>
                        {editMode ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Vehicle Year</label>
                              <input
                                type="number"
                                value={editingLead.vehicle_year || ''}
                                onChange={(e) => setEditingLead({...editingLead, vehicle_year: e.target.value})}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="e.g., 2024"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Vehicle Make</label>
                              <input
                                type="text"
                                value={editingLead.vehicle_make || ''}
                                onChange={(e) => setEditingLead({...editingLead, vehicle_make: e.target.value})}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="e.g., Toyota"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Vehicle Model</label>
                              <input
                                type="text"
                                value={editingLead.vehicle_model || ''}
                                onChange={(e) => setEditingLead({...editingLead, vehicle_model: e.target.value})}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                placeholder="e.g., Camry"
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Budget Range</label>
                              <div className="flex space-x-2">
                                <input
                                  type="number"
                                  value={editingLead.budget_min || ''}
                                  onChange={(e) => setEditingLead({...editingLead, budget_min: parseInt(e.target.value)})}
                                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  placeholder="Min"
                                />
                                <input
                                  type="number"
                                  value={editingLead.budget_max || ''}
                                  onChange={(e) => setEditingLead({...editingLead, budget_max: parseInt(e.target.value)})}
                                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                                  placeholder="Max"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            {(selectedLead.vehicle_make || selectedLead.vehicle_model) ? (
                              <>
                                <p>{selectedLead.vehicle_year} {selectedLead.vehicle_make} {selectedLead.vehicle_model}</p>
                                {selectedLead.budget_min && selectedLead.budget_max && (
                                  <p className="text-sm text-gray-600">
                                    Budget: ${selectedLead.budget_min?.toLocaleString()} - ${selectedLead.budget_max?.toLocaleString()}
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-gray-500">No vehicle interest specified</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Message */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Message</h4>
                        {editMode ? (
                          <textarea
                            value={editingLead.message || ''}
                            onChange={(e) => setEditingLead({...editingLead, message: e.target.value})}
                            className="w-full"
                            rows="4"
                            placeholder="Enter message or notes about this lead..."
                          />
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-700">{selectedLead.message || 'No message'}</p>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {selectedLead.notes && selectedLead.notes.length > 0 ? (
                            selectedLead.notes.map((note) => (
                              <div key={note.id} className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-sm text-gray-700">{note.text}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(note.created_at).toLocaleString()} - {note.created_by}
                                </p>
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500 text-sm">No notes yet</p>
                          )}
                        </div>
                        
                        {/* Add Note */}
                        <div className="mt-4 flex space-x-2">
                          <input
                            type="text"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add a note..."
                            className="flex-1 "
                          />
                          <button
                            onClick={() => handleAddNote(selectedLead.id)}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 rounded-adm-ctl flex items-center space-x-2 transition-colors"
                          >
                            <SafeIcon icon={FiMessageSquare} className="w-4 h-4" />
                            <span>Add</span>
                          </button>
                        </div>
                      </div>
                    </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Lead Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[var(--adm-scrim)] backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b">
                <h2 className="text-2xl font-bold">Add New Lead</h2>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newLead.first_name}
                      onChange={(e) => setNewLead({...newLead, first_name: e.target.value})}
                      className="w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newLead.last_name}
                      onChange={(e) => setNewLead({...newLead, last_name: e.target.value})}
                      className="w-full"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                      className="w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={newLead.phone}
                      onChange={(e) => setNewLead({...newLead, phone: e.target.value})}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lead Type</label>
                    <select
                      value={newLead.lead_type}
                      onChange={(e) => setNewLead({...newLead, lead_type: e.target.value})}
                      className="w-full"
                    >
                      <option value="information">Information</option>
                      <option value="test_drive">Test Drive</option>
                      <option value="financing">Financing</option>
                      <option value="trade_in">Trade-In</option>
                      <option value="service">Service</option>
                      <option value="parts">Parts</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={newLead.priority}
                      onChange={(e) => setNewLead({...newLead, priority: e.target.value})}
                      className="w-full"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Interest</label>
                  <input
                    type="text"
                    value={newLead.vehicle_interest}
                    onChange={(e) => setNewLead({...newLead, vehicle_interest: e.target.value})}
                    placeholder="e.g., 2019 Ford F-150"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Range</label>
                  <input
                    type="text"
                    value={newLead.budget_range}
                    onChange={(e) => setNewLead({...newLead, budget_range: e.target.value})}
                    placeholder="e.g., $20,000 - $30,000"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newLead.notes}
                    onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                    rows={3}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="p-6 border-t flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddLead}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Add Lead
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadManagement;
